// ============================================================
// 生成 Schema（阶段一 T1）：可视化大屏（dtwince.dashboard.schema.json）
// 落库映射：pages.config + components（apps/dtwin-server/src/modules/pages/entities/*）
// 字段来源：VisualComponent / DataSourceConfig / PageConfig（packages/shared/src/types/index.ts）
// ============================================================
import { z } from 'zod';
import { GENERATION_SCHEMA_VERSION, GEN_LIMITS, WIDGET_DATASOURCE_TYPES } from './constants';
import { bundleMetaSchema, dataFieldMappingSchema, safeParserSchema } from './common';

/** 组件数据源（V1 安全子集：static/api/device/mqtt；parser 禁止 JS 转换） */
export const componentDataSourceSchema = z
  .object({
    dataSetId: z.string().max(64).optional(),
    type: z.enum(WIDGET_DATASOURCE_TYPES),
    config: z.record(z.unknown()).default({}),
    refreshInterval: z.number().int().positive().max(3600000).optional(),
    mapping: z.array(dataFieldMappingSchema).max(50).optional(),
    parser: safeParserSchema.optional(),
  })
  .strict();
export type ComponentDataSourceSpec = z.infer<typeof componentDataSourceSchema>;

/** 大屏组件（components 表 + 四段式契约） */
export const dashboardComponentSchema = z
  .object({
    type: z.string().min(1).max(64),
    position: z.object({ x: z.number(), y: z.number() }).strict().default({ x: 0, y: 0 }),
    size: z
      .object({ width: z.number().positive().max(20000), height: z.number().positive().max(20000) })
      .strict()
      .default({ width: 300, height: 200 }),
    dataSource: componentDataSourceSchema.optional(),
    style: z.record(z.unknown()).default({}),
    config: z.record(z.unknown()).default({}),
    base: z.record(z.unknown()).default({}),
    special: z.record(z.unknown()).default({}),
    events: z.array(z.record(z.unknown())).max(20).default([]),
  })
  .strict();
export type DashboardComponentSpec = z.infer<typeof dashboardComponentSchema>;

/** 大屏页（pages.config） */
export const dashboardPageSchema = z
  .object({
    name: z.string().min(1).max(120),
    config: z
      .object({
        width: z.number().int().positive().max(20000),
        height: z.number().int().positive().max(20000),
        backgroundColor: z.string().max(100).default('#0b1a2a'),
        backgroundImage: z.string().max(500).optional(),
        editorView: z
          .object({ scale: z.number(), offsetX: z.number(), offsetY: z.number() })
          .strict()
          .optional(),
      })
      .strict(),
  })
  .strict();
export type DashboardPageSpec = z.infer<typeof dashboardPageSchema>;

export const dashboardBundleSchema = z
  .object({
    schemaVersion: z.literal(GENERATION_SCHEMA_VERSION),
    kind: z.literal('dashboard'),
    meta: bundleMetaSchema,
    page: dashboardPageSchema,
    components: z.array(dashboardComponentSchema).max(GEN_LIMITS.components).default([]),
  })
  .strict();

export type DashboardBundle = z.infer<typeof dashboardBundleSchema>;
